import React, { useState } from 'react';
import { XMarkIcon, PlayIcon } from '@heroicons/react/24/outline';
import type { DryRunResult } from '../../../api/databaseMigrations';

interface Props {
  result: DryRunResult;
  targets: string[]; // company IDs or ['__registry__']
  isRegistry: boolean;
  onClose: () => void;
  onConfirm: (opts: { backup: boolean }) => void;
}

const KIND_BADGE: Record<string, string> = {
  DDL: 'bg-amber-100 text-amber-700',
  DML: 'bg-blue-100 text-blue-700',
  DESTRUCTIVE: 'bg-red-100 text-red-700',
  READ: 'bg-slate-100 text-slate-600',
};

const DryRunPreviewModal: React.FC<Props> = ({ result, targets, isRegistry, onClose, onConfirm }) => {
  const hasDestructive = result.statements.some((s) => s.kind === 'DESTRUCTIVE');
  const requiresConfirm = hasDestructive || isRegistry;
  const [backup, setBackup] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const canExecute = !requiresConfirm || confirmText === 'CONFIRM';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Dry-run preview</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><XMarkIcon className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <section>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Statements ({result.statements.length})</h3>
            <ul className="space-y-2">
              {result.statements.map((s, i) => (
                <li key={i} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${KIND_BADGE[s.kind]}`}>{s.kind}</span>
                    <span className="text-xs text-slate-400">#{i + 1}</span>
                  </div>
                  <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap">{s.sql}</pre>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Per-target preview ({targets.length})</h3>
            <ul className="space-y-2">
              {targets.map((t) => {
                const preview = result.previews[isRegistry ? '__registry__' : t];
                return (
                  <li key={t} className="border border-slate-200 rounded-lg p-3">
                    <div className="text-sm font-mono text-slate-800 mb-2">{t}</div>
                    {preview?.error && (
                      <div className="text-xs text-red-600">Error: {preview.error}</div>
                    )}
                    {!preview?.error && (
                      <ul className="text-xs text-slate-600 space-y-1">
                        {result.statements.map((_, i) => (
                          <li key={i}>
                            #{i + 1}:{' '}
                            {preview?.rowCounts[i] != null && <>would affect ~{preview.rowCounts[i]} rows</>}
                            {preview?.schemaChecks[i] && (
                              <>
                                table <code>{preview.schemaChecks[i]!.name}</code>{' '}
                                {preview.schemaChecks[i]!.tableExists ? 'exists' : 'NOT FOUND'}
                              </>
                            )}
                            {preview?.rowCounts[i] == null && !preview?.schemaChecks[i] && <>(no preview)</>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>

        <div className="border-t border-slate-200 p-5 space-y-3">
          {!isRegistry && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={backup} onChange={(e) => setBackup(e.target.checked)} />
              Take a mysqldump backup of each target before running (required for rollback)
            </label>
          )}
          {requiresConfirm && (
            <div>
              <p className="text-sm text-red-700 mb-1">
                {isRegistry
                  ? 'This will modify the REGISTRY DB. Type CONFIRM to proceed.'
                  : 'This run includes destructive statements. Type CONFIRM to proceed.'}
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
                className="w-48 px-3 py-1.5 border border-red-300 rounded-lg text-sm font-mono"
              />
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={() => onConfirm({ backup })}
              disabled={!canExecute}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <PlayIcon className="w-4 h-4" /> Execute
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DryRunPreviewModal;
